/**
 * Phase 115 — Super Admin · Academy Acquisition CRM (B2B pipeline).
 *
 * Tracks INCOMING ACADEMIES as a B2B sales funnel.  Completely isolated
 * from the academy-level Student CRM (`/crm` → `CRM.js`).
 *
 *   Inbound Inquiries  →  Contacted  →  Demo Scheduled  →  Trial Active  →  Won
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Building2, Plus, Search, Phone, Mail, MapPin, MoreHorizontal,
  Trash2, LayoutGrid, List, Inbox, MessageCircle, CalendarCheck,
  Hourglass, Trophy, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import Phase150OutboundPanel from '@/components/admin/Phase150OutboundPanel';
import Phase152CadencePanel from '@/components/admin/Phase152CadencePanel';

// 5 B2B Kanban lanes (CEO-locked stage order).
const B2B_STAGES = [
  { id: 'inbound',        label: 'Inbound Inquiries', icon: Inbox,         color: 'bg-sky-500',     light: 'bg-sky-50 border-sky-200 dark:bg-sky-950/20',     text: 'text-sky-700' },
  { id: 'contacted',      label: 'Contacted',         icon: MessageCircle, color: 'bg-indigo-500',  light: 'bg-indigo-50 border-indigo-200 dark:bg-indigo-950/20', text: 'text-indigo-700' },
  { id: 'demo_scheduled', label: 'Demo Scheduled',    icon: CalendarCheck, color: 'bg-amber-500',   light: 'bg-amber-50 border-amber-200 dark:bg-amber-950/20', text: 'text-amber-700' },
  { id: 'trial_active',   label: 'Trial Active',      icon: Hourglass,     color: 'bg-orange-500',  light: 'bg-orange-50 border-orange-200 dark:bg-orange-950/20', text: 'text-orange-700' },
  { id: 'won',            label: 'Won',               icon: Trophy,        color: 'bg-emerald-500', light: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20', text: 'text-emerald-700' },
];

const SPORT_VERTICALS = ['Football', 'Cricket', 'Hockey', 'Badminton', 'Multi-Sport'];
const TIER_OPTIONS = [
  { value: 'starter_trial', label: 'Starter Trial' },
  { value: 'pro',           label: 'Pro Plan' },
  { value: 'legend',        label: 'Legend Plan' },
];

const EMPTY_FORM = {
  academy_name: '', contact_name: '', email: '', phone: '', city: '',
  sport_vertical: '', target_tier: '', estimated_centers: '', notes: '',
};

export default function SuperAdminAcademyCRM() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('kanban');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(null); // lead.id currently mid-send

  const fetchData = useCallback(async () => {
    try {
      const [lRes, sRes] = await Promise.all([
        api.get('/super-admin/crm/leads', { params: { search: search || undefined } }),
        api.get('/super-admin/crm/stats'),
      ]);
      setLeads(lRes.data || []);
      setStats(sRes.data || null);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to load CRM');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.academy_name.trim()) {
      toast.error('Academy Name is required');
      return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error('Provide at least email or phone');
      return;
    }
    try {
      const payload = {
        ...form,
        estimated_centers: form.estimated_centers === ''
          ? null
          : Number(form.estimated_centers) || null,
      };
      await api.post('/super-admin/crm/leads', payload);
      toast.success('Academy lead added');
      setShowAdd(false);
      setForm(EMPTY_FORM);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add lead');
    }
  };

  const handleStageChange = async (leadId, newStage) => {
    try {
      await api.put(`/super-admin/crm/leads/${leadId}`, { b2b_stage: newStage });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, b2b_stage: newStage } : l));
      const target = B2B_STAGES.find(s => s.id === newStage);
      toast.success(`Moved to ${target?.label || newStage}`);
      // refresh stats only
      api.get('/super-admin/crm/stats').then(r => setStats(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move');
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Delete this academy lead?')) return;
    try {
      await api.delete(`/super-admin/crm/leads/${leadId}`);
      toast.success('Deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleSendWhatsApp = async (lead) => {
    if (!lead?.phone) {
      toast.error('No phone number on this lead');
      return;
    }
    if (!window.confirm(
      `Send the demo pitch via WhatsApp to ${lead.academy_name || lead.contact_name || 'this lead'} at ${lead.phone}? ` +
      `The lead will move to "Contacted".`
    )) return;
    setSendingWhatsApp(lead.id);
    try {
      const r = await api.post(`/super-admin/crm/leads/${lead.id}/whatsapp-demo`);
      const wasMocked = r.data?.mocked;
      toast.success(
        wasMocked
          ? 'WhatsApp queued (Twilio mocked — set credentials to send live)'
          : 'WhatsApp demo pitch sent · moved to Contacted',
      );
      // Optimistically apply the returned lead state to avoid a full refetch flicker.
      if (r.data?.lead) {
        setLeads(prev => prev.map(l => l.id === lead.id ? r.data.lead : l));
        api.get('/super-admin/crm/stats').then(s => setStats(s.data)).catch(() => {});
      } else {
        fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'WhatsApp send failed');
    } finally {
      setSendingWhatsApp(null);
    }
  };

  const visibleLeads = useMemo(() => {
    if (!search.trim()) return leads;
    const s = search.toLowerCase();
    return leads.filter(l =>
      (l.academy_name || '').toLowerCase().includes(s) ||
      (l.contact_name || '').toLowerCase().includes(s) ||
      (l.email || '').toLowerCase().includes(s) ||
      (l.phone || '').includes(s) ||
      (l.city || '').toLowerCase().includes(s),
    );
  }, [leads, search]);

  const stageLeads = (stageId) => visibleLeads.filter(l => (l.b2b_stage || 'inbound') === stageId);

  const LeadCard = ({ lead, compact = false }) => {
    const stage = B2B_STAGES.find(s => s.id === (lead.b2b_stage || 'inbound')) || B2B_STAGES[0];
    const isInbound = (lead.b2b_stage || 'inbound') === 'inbound';
    const canWhatsApp = isInbound && !!(lead.phone || '').trim();
    const isSending = sendingWhatsApp === lead.id;
    return (
      <div
        className={`p-3 rounded-lg border ${compact ? '' : stage.light} bg-card hover:shadow-md transition-shadow`}
        data-testid={`b2b-lead-${lead.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm text-foreground truncate">
              {lead.academy_name || lead.contact_name || '—'}
            </p>
            {lead.contact_name && lead.contact_name !== lead.academy_name && (
              <p className="text-xs text-muted-foreground">Contact: {lead.contact_name}</p>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`b2b-lead-menu-${lead.id}`}>
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {B2B_STAGES.filter(s => s.id !== (lead.b2b_stage || 'inbound')).map(s => (
                <DropdownMenuItem
                  key={s.id}
                  onClick={() => handleStageChange(lead.id, s.id)}
                  data-testid={`b2b-move-${lead.id}-${s.id}`}
                >
                  <div className={`h-2 w-2 rounded-full ${s.color} mr-2`} /> Move to {s.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleDelete(lead.id)}
                className="text-destructive"
                data-testid={`b2b-delete-${lead.id}`}
              >
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {lead.city && (
            <Badge variant="outline" className="text-[10px] h-5">
              <MapPin className="h-2.5 w-2.5 mr-0.5" /> {lead.city}
            </Badge>
          )}
          {lead.sport_vertical && (
            <Badge variant="outline" className="text-[10px] h-5">{lead.sport_vertical}</Badge>
          )}
          {lead.target_tier && (
            <Badge variant="secondary" className="text-[10px] h-5 capitalize">
              {(TIER_OPTIONS.find(t => t.value === lead.target_tier)?.label) || lead.target_tier}
            </Badge>
          )}
          {lead.estimated_centers != null && (
            <Badge variant="outline" className="text-[10px] h-5">
              {lead.estimated_centers} centers
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
          {lead.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone}</span>}
          {lead.email && <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3" />{lead.email}</span>}
        </div>
        {(lead.message || lead.notes) && (
          <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2 italic">
            "{lead.message || lead.notes}"
          </p>
        )}
        {lead.source === 'landing_contact_form' && (
          <Badge className="bg-sky-100 text-sky-700 text-[10px] mt-2" data-testid={`b2b-lead-${lead.id}-landing-tag`}>
            landing_contact
          </Badge>
        )}
        {canWhatsApp && (
          <Button
            size="sm"
            variant="outline"
            className="w-full mt-2 h-7 text-[11px] border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 dark:bg-emerald-950/30 dark:hover:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800"
            onClick={() => handleSendWhatsApp(lead)}
            disabled={isSending}
            data-testid={`b2b-whatsapp-demo-btn-${lead.id}`}
          >
            <Send className="h-3 w-3 mr-1" />
            {isSending ? 'Sending…' : 'Send WhatsApp Demo Pitch'}
          </Button>
        )}
        {lead.last_whatsapp_at && (
          <p
            className="text-[10px] text-muted-foreground mt-1 italic"
            data-testid={`b2b-last-whatsapp-${lead.id}`}
          >
            Last WhatsApp · {new Date(lead.last_whatsapp_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
          </p>
        )}
      </div>
    );
  };

  if (loading) return (
    <div className="space-y-4" data-testid="super-admin-academy-crm-page-loading">
      <Skeleton className="h-8 w-72" />
      <div className="grid grid-cols-5 gap-3">
        {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-64 rounded-xl" />)}
      </div>
    </div>
  );

  const totalAcademies = stats?.total || 0;

  return (
    <div className="space-y-5" data-testid="super-admin-academy-crm-page">
      {/* Phase 150 — Zero-Touch Outbound Engine */}
      <Phase150OutboundPanel onLeadsRefresh={fetchData} />

      {/* Phase 152 — Tiered Touch #2 Cadence */}
      <Phase152CadencePanel onLeadsRefresh={fetchData} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1
            className="text-2xl font-display font-semibold flex items-center gap-2"
            data-testid="super-admin-academy-crm-title"
          >
            <Building2 className="h-5 w-5 text-primary" />
            Academy Acquisition CRM
          </h1>
          <p className="text-sm text-muted-foreground" data-testid="super-admin-academy-crm-subtitle">
            {totalAcademies} {totalAcademies === 1 ? 'academy' : 'academies'} in pipeline
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-muted rounded-lg p-0.5">
            <Button
              variant={viewMode === 'kanban' ? 'default' : 'ghost'}
              size="sm" className="h-7 text-xs rounded-md"
              onClick={() => setViewMode('kanban')}
              data-testid="b2b-view-kanban-btn"
            >
              <LayoutGrid className="h-3.5 w-3.5 mr-1" /> Kanban
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm" className="h-7 text-xs rounded-md"
              onClick={() => setViewMode('list')}
              data-testid="b2b-view-list-btn"
            >
              <List className="h-3.5 w-3.5 mr-1" /> List
            </Button>
          </div>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-academy-lead-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Academy Lead
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle data-testid="add-academy-lead-modal-title">Add Academy Lead</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAdd} className="space-y-3 max-h-[70vh] overflow-y-auto" data-testid="add-academy-lead-form">
                <div className="space-y-1.5">
                  <Label className="text-xs">Academy Name *</Label>
                  <Input
                    value={form.academy_name}
                    onChange={(e) => setForm({...form, academy_name: e.target.value})}
                    placeholder="Champions Academy"
                    required
                    data-testid="b2b-academy-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Primary Email</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({...form, email: e.target.value})}
                      placeholder="owner@academy.in"
                      data-testid="b2b-email-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Phone Number</Label>
                    <Input
                      value={form.phone}
                      onChange={(e) => setForm({...form, phone: e.target.value})}
                      placeholder="+91..."
                      data-testid="b2b-phone-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Owner / Contact Name</Label>
                    <Input
                      value={form.contact_name}
                      onChange={(e) => setForm({...form, contact_name: e.target.value})}
                      placeholder="Founder / decision-maker"
                      data-testid="b2b-contact-name-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">City / Base Location</Label>
                    <Input
                      value={form.city}
                      onChange={(e) => setForm({...form, city: e.target.value})}
                      placeholder="Vijayawada, Bengaluru..."
                      data-testid="b2b-city-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Prominent Sport Vertical</Label>
                    <Select
                      value={form.sport_vertical}
                      onValueChange={(v) => setForm({...form, sport_vertical: v})}
                    >
                      <SelectTrigger data-testid="b2b-sport-vertical-trigger">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {SPORT_VERTICALS.map(s => (
                          <SelectItem key={s} value={s} data-testid={`b2b-sport-vertical-${s}`}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Target Pricing Tier</Label>
                    <Select
                      value={form.target_tier}
                      onValueChange={(v) => setForm({...form, target_tier: v})}
                    >
                      <SelectTrigger data-testid="b2b-target-tier-trigger">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIER_OPTIONS.map(t => (
                          <SelectItem key={t.value} value={t.value} data-testid={`b2b-target-tier-${t.value}`}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Estimated Number of Centers</Label>
                  <Input
                    type="number" min="0"
                    value={form.estimated_centers}
                    onChange={(e) => setForm({...form, estimated_centers: e.target.value})}
                    placeholder="3"
                    data-testid="b2b-centers-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Notes</Label>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => setForm({...form, notes: e.target.value})}
                    placeholder="How did we find them? Decision timeline?"
                    rows={2}
                    data-testid="b2b-notes-input"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="b2b-submit-lead-btn">
                  Add Academy Lead
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Pipeline counts */}
      {stats && (
        <div className="flex flex-wrap gap-2" data-testid="b2b-pipeline-stats">
          <Badge variant="outline" className="text-xs h-7 px-3">
            All ({stats.total})
          </Badge>
          {B2B_STAGES.map(s => {
            const Icon = s.icon;
            return (
              <Badge
                key={s.id}
                variant="outline"
                className="text-xs h-7 px-3 inline-flex items-center gap-1.5"
                data-testid={`b2b-stat-${s.id}`}
              >
                <Icon className={`h-3 w-3 ${s.text}`} />
                {s.label} ({stats.by_stage?.[s.id] || 0})
              </Badge>
            );
          })}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search academies, contacts, cities..."
          className="pl-9 h-9"
          data-testid="b2b-search-input"
        />
      </div>

      {/* KANBAN VIEW */}
      {viewMode === 'kanban' && (
        <div
          className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3"
          data-testid="b2b-kanban-board"
        >
          {B2B_STAGES.map(stage => {
            const items = stageLeads(stage.id);
            const Icon = stage.icon;
            return (
              <div key={stage.id} className="space-y-2" data-testid={`b2b-lane-${stage.id}`}>
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2">
                    <Icon className={`h-3.5 w-3.5 ${stage.text}`} />
                    <span className="text-sm font-semibold">{stage.label}</span>
                  </div>
                  <Badge variant="secondary" className="text-[10px] h-5 tabular-nums">
                    {items.length}
                  </Badge>
                </div>
                <div className="space-y-2 min-h-[200px] p-2 rounded-xl bg-muted/30 border border-dashed border-border">
                  {items.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-8">
                      No academies
                    </p>
                  ) : items.map(lead => <LeadCard key={lead.id} lead={lead} />)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* LIST VIEW */}
      {viewMode === 'list' && (
        <div className="space-y-2" data-testid="b2b-list-view">
          {visibleLeads.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Building2 className="h-12 w-12 text-muted-foreground/30 mb-4" />
                <h3 className="text-lg font-semibold mb-1">No academy leads yet</h3>
                <p className="text-sm text-muted-foreground">
                  Click "Add Academy Lead" to start building the B2B pipeline
                </p>
              </CardContent>
            </Card>
          ) : visibleLeads.map(lead => <LeadCard key={lead.id} lead={lead} compact />)}
        </div>
      )}
    </div>
  );
}
